import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { GBP, statusColor, pretty } from "@/lib/helpers";
import { PageHeader } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users } from "lucide-react";
import { toast } from "sonner";

export default function Tables() {
  const { rid } = useParams();
  const [tables, setTables] = useState([]);
  const [orders, setOrders] = useState({});
  const [bookings, setBookings] = useState([]);
  const [walkin, setWalkin] = useState(null);
  const [guests, setGuests] = useState(2);

  const load = useCallback(async () => {
    const t = await api.get(`/restaurants/${rid}/tables`);
    setTables(t.data);
    api.get(`/restaurants/${rid}/bookings`, { params: { today: true } }).then((r) => setBookings(r.data)).catch(() => {});
    const occ = t.data.filter((x) => x.current_order_id);
    const map = {};
    await Promise.all(occ.map(async (x) => {
      try { const o = await api.get(`/orders/${x.current_order_id}`); map[x.id] = o.data; } catch {}
    }));
    setOrders(map);
  }, [rid]);
  useEffect(() => { load(); }, [load]);

  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const toMin = (hhmm) => { const [h, m] = (hhmm || "0:0").split(":").map(Number); return h * 60 + (m || 0); };
  // reservation window: 30 min before to 120 min after booking time
  const reservationFor = (tid) => {
    const active = bookings.filter((b) => (b.table_ids || []).includes(tid) && ["PENDING", "CONFIRMED"].includes(b.status));
    const inWindow = active.find((b) => { const s = toMin(b.booking_time); return nowMin >= s - 30 && nowMin <= s + 120; });
    if (inWindow) return { type: "now", booking: inWindow };
    const upcoming = active.filter((b) => toMin(b.booking_time) > nowMin).sort((a, b) => toMin(a.booking_time) - toMin(b.booking_time))[0];
    return upcoming ? { type: "upcoming", booking: upcoming } : null;
  };

  const startWalkin = async () => {
    await api.post(`/restaurants/${rid}/tables/${walkin.id}/start-walkin`, { guest_count: +guests, customer_name: "Walk-in" });
    toast.success(`Seated at ${walkin.table_name}`); setWalkin(null); load();
  };
  const closeTable = async (t) => {
    await api.post(`/restaurants/${rid}/tables/${t.id}/close`);
    toast.success(`${t.table_name} closed`); load();
  };

  return (
    <div>
      <PageHeader title="Dine-in tables" subtitle="Floor view — seat walk-ins, track status and close tables." />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {tables.map((t) => {
          const ord = orders[t.id];
          const occupied = t.status === "OCCUPIED";
          const resv = !occupied ? reservationFor(t.id) : null;
          const reservedNow = resv?.type === "now";
          return (
            <Card key={t.id} data-testid={`table-card-${t.id}`}
              className={`p-5 rounded-2xl border-2 ${occupied ? "border-accent/40" : reservedNow ? "border-amber-400/60" : "border-border"}`}>
              <div className="flex items-center justify-between">
                <p className="font-display font-bold text-lg">{t.table_name}</p>
                <Badge className={reservedNow ? "bg-amber-100 text-amber-700 border-amber-200" : statusColor(t.status)} variant="outline">
                  {reservedNow ? "Reserved" : pretty(t.status)}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {t.capacity} seats</p>
              {resv && (
                <div data-testid={`table-reservation-${t.id}`} className={`mt-2 rounded-lg p-2 text-xs ${reservedNow ? "bg-amber-50 text-amber-800" : "bg-secondary text-muted-foreground"}`}>
                  {reservedNow ? "Reserved now" : "Next"}: {resv.booking.booking_time} · {resv.booking.customer_name} ({resv.booking.guest_count})
                </div>
              )}
              {occupied && ord && (
                <div className="mt-3 rounded-lg bg-secondary p-2.5">
                  <p className="text-sm font-medium">{ord.order_number}</p>
                  <p className="text-sm text-muted-foreground">{ord.items.length} items · {GBP(ord.total)}</p>
                  <Badge className={statusColor(ord.payment_status)} variant="outline">{pretty(ord.payment_status)}</Badge>
                </div>
              )}
              <div className="mt-3">
                {occupied ? (
                  <Button variant="outline" className="w-full h-11" data-testid={`close-table-${t.id}`} onClick={() => closeTable(t)}>Close & complete</Button>
                ) : (
                  <Button className="w-full h-11" data-testid={`seat-table-${t.id}`} onClick={() => { setWalkin(t); setGuests(2); }}>Seat walk-in</Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>
      {tables.length === 0 && <p className="text-muted-foreground mt-6">No tables yet. Add tables in Settings.</p>}

      <Dialog open={!!walkin} onOpenChange={(o) => !o && setWalkin(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display">Seat walk-in at {walkin?.table_name}</DialogTitle></DialogHeader>
          <div><Label>Number of guests</Label>
            <Input type="number" min={1} className="h-11 mt-1.5" value={guests} onChange={(e) => setGuests(e.target.value)} data-testid="walkin-guests" /></div>
          <DialogFooter><Button className="h-11" onClick={startWalkin} data-testid="confirm-walkin">Start dine-in order</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
